/* Owl + Fox Shop — Main JavaScript
   Uses Alpine.js (loaded via CDN in theme.liquid) for reactivity.
   Handles: cart state, search overlay, scroll reveal, nav behaviour. */

/* ============================================================
   ZAR FORMATTER
   ============================================================ */
window.ofR = function(n) {
  return 'R ' + Math.round(n).toLocaleString('en-ZA').replace(/,/g, ' ');
};

/* ============================================================
   SCROLL REVEAL
   ============================================================ */
(function() {
  if (!window.IntersectionObserver) return;
  const obs = new IntersectionObserver(
    (entries) => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); obs.unobserve(e.target); } }),
    { threshold: 0.08, rootMargin: '-8% 0px' }
  );
  function observe() { document.querySelectorAll('.reveal:not(.in)').forEach(el => obs.observe(el)); }
  observe();
  document.addEventListener('shopify:section:load', observe);
})();

/* ============================================================
   NAV SCROLL BLUR
   ============================================================ */
(function() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 8);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

/* ============================================================
   ALPINE.JS STORE: Cart
   ============================================================ */
document.addEventListener('alpine:init', function() {

  Alpine.store('cart', {
    open: false,
    items: [],
    count: 0,
    total: 0,
    loading: false,

    async init() {
      await this.fetch();
    },

    async fetch() {
      try {
        const r = await fetch('/cart.js');
        const data = await r.json();
        this.items = data.items;
        this.count = data.item_count;
        this.total = data.total_price / 100;
      } catch(e) { /* silent */ }
    },

    /* Returns true on success, false if Shopify refused the add (e.g. the item
       sold out between page load and click — DENY inventory policy). On failure
       the cart drawer is deliberately NOT opened, so a customer never sees an
       empty drawer pop up after a failed add. */
    async add(variantId, qty, properties) {
      this.loading = true;
      let ok = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty || 1, properties: properties || {} }),
        });
        ok = res.ok;
        await this.fetch();
        if (ok) this.open = true;
      } catch (e) {
        ok = false;
      } finally {
        this.loading = false;
      }
      return ok;
    },

    async updateQty(key, qty) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      await this.fetch();
    },

    async remove(key) {
      await this.updateQty(key, 0);
    },

    get freeShippingRemaining() {
      const threshold = 1500;
      const remaining = threshold - this.total;
      return remaining > 0 ? remaining : 0;
    },

    get freeShippingPercent() {
      return Math.min(100, (this.total / 1500) * 100);
    },

    get freeShipping() {
      return this.total >= 1500;
    },
  });

  /* ============================================================
     ALPINE COMPONENT: Cart Drawer
     ============================================================ */
  Alpine.data('cartDrawer', function() {
    return {
      get open() { return this.$store.cart.open; },
      set open(v) { this.$store.cart.open = v; },
      close() { this.$store.cart.open = false; },
    };
  });

  /* ============================================================
     ALPINE COMPONENT: Search Overlay
     ============================================================ */
  Alpine.data('searchOverlay', function() {
    return {
      open: false,
      query: '',
      toggle() { this.open = !this.open; if (this.open) this.$nextTick(() => this.$el.querySelector('input')?.focus()); },
      close() { this.open = false; this.query = ''; },
    };
  });

  /* ============================================================
     ALPINE COMPONENT: Accordion
     ============================================================ */
  Alpine.data('accordion', function() {
    return {
      openIndex: null,
      toggle(i) { this.openIndex = this.openIndex === i ? null : i; },
      isOpen(i) { return this.openIndex === i; },
    };
  });

  /* ============================================================
     ALPINE COMPONENT: Product Variant Selector
     ============================================================ */
  Alpine.data('productForm', function() {
    return {
      qty: 1,
      selectedOptions: {},
      currentVariantId: 0,
      currentPrice: 0,
      currentComparePrice: 0,
      currentAvailable: true,
      adding: false,
      added: false,

      init() {
        const cv = window.OF_CURRENT_VARIANT;
        if (cv) {
          this.currentVariantId = cv.id;
          this.currentPrice     = cv.price;
          this.currentComparePrice = cv.compare_price;
          this.currentAvailable = cv.available;
          // cv.options is {"1":"Almond","2":"60x60"} — coerce keys to ints
          this.selectedOptions = Object.fromEntries(
            Object.entries(cv.options).map(([k, v]) => [parseInt(k), v])
          );
        }
        // highlight the initial variant's image (falls back to a fuzzy match
        // when the variant has no image of its own assigned in Shopify)
        const initialVariant = this._findVariant() || (cv ? { image_id: cv.image_id, options: this._orderedOptionValues(cv) } : null);
        if (initialVariant) this._showVariantImage(initialVariant, false);

        // watch ATC button for sticky bar
        const atcBtn = document.querySelector('[data-atc-main]');
        if (atcBtn) {
          const obs = () => { this.showStickyAtc = atcBtn.getBoundingClientRect().bottom < 80; };
          window.addEventListener('scroll', obs, { passive: true });
        }
      },

      selectOption(position, value) {
        this.selectedOptions = { ...this.selectedOptions, [position]: value };
        const variant = this._findVariant();
        if (!variant) return;
        this.currentVariantId    = variant.id;
        this.currentPrice        = variant.price;
        this.currentComparePrice = variant.compare_price;
        this.currentAvailable    = variant.available;
        this._showVariantImage(variant, true);
      },

      _findVariant() {
        const opts = this.selectedOptions;
        return (window.OF_PRODUCT_VARIANTS || []).find(v =>
          v.options.every((val, i) => opts[i + 1] === val)
        ) || null;
      },

      // window.OF_CURRENT_VARIANT.options is keyed by position ({"1":"Almond"});
      // OF_PRODUCT_VARIANTS entries use a plain ordered array. Convert the former
      // to the latter so both shapes can be matched the same way.
      _orderedOptionValues(cv) {
        if (!cv || !cv.options) return [];
        return Object.keys(cv.options)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map(k => cv.options[k]);
      },

      /* Shows the image for `variant` in the gallery: uses the variant's own
         assigned image when Shopify has one, otherwise falls back to matching
         the variant's option values (e.g. "Midnight", "Fossil") against each
         gallery image's alt text and filename — covers the common case in this
         catalogue where a color/finish variant was never given its own
         Shopify-side image assignment, so nothing would otherwise change when
         a customer picks a swatch. */
      _showVariantImage(variant, scroll) {
        let frame = null;
        if (variant && variant.image_id) {
          frame = document.querySelector('.pdp-gallery__frame[data-img-id="' + variant.image_id + '"]');
        }
        if (!frame) {
          frame = this._matchImageFrame(variant);
        }

        document.querySelectorAll('.pdp-gallery__frame').forEach(f => {
          f.classList.toggle('pdp-frame--active', f === frame);
        });

        if (!frame || !scroll) return;
        const gallery = frame.closest('.pdp-gallery');
        // mobile: horizontal scroll container; desktop: scroll page
        if (gallery && window.getComputedStyle(gallery).overflowX !== 'visible') {
          gallery.scrollTo({ left: frame.offsetLeft - 16, behavior: 'smooth' });
        } else {
          const top = frame.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      },

      _tokenize(str) {
        return (str || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
      },

      _matchImageFrame(variant) {
        const optionTokens = (variant && variant.options || []).flatMap(v => this._tokenize(v));
        if (!optionTokens.length) return null;

        const frames = Array.from(document.querySelectorAll('.pdp-gallery__frame'));
        let best = null, bestScore = 0;
        frames.forEach(frame => {
          const img = frame.querySelector('img');
          if (!img) return;
          const haystack = this._tokenize(img.alt).concat(this._tokenize(img.getAttribute('src')));
          let score = 0;
          optionTokens.forEach(t => { if (haystack.includes(t)) score++; });
          if (score > bestScore) { bestScore = score; best = frame; }
        });
        return bestScore > 0 ? best : null;
      },

      formatPrice(cents) {
        return 'R ' + Math.round(cents / 100).toLocaleString('en-US').replace(/,/g, ' ');
      },

      async addToCart() {
        if (!this.currentVariantId || this.adding || !this.currentAvailable) return;
        this.adding = true;
        await this.$store.cart.add(this.currentVariantId, this.qty);
        this.adding = false;
        this.added = true;
        setTimeout(() => { this.added = false; }, 1400);
      },

      incQty() { this.qty = Math.min(this.qty + 1, 99); },
      decQty() { this.qty = Math.max(this.qty - 1, 1); },
    };
  });

  /* ============================================================
     ALPINE COMPONENT: Sticky ATC Bar
     ============================================================ */
  Alpine.data('stickyAtc', function() {
    return {
      visible: false,
      init() {
        const atcBtn = document.querySelector('[data-atc-main]');
        if (!atcBtn) return;
        const obs = () => {
          const rect = atcBtn.getBoundingClientRect();
          this.visible = rect.bottom < 80;
        };
        window.addEventListener('scroll', obs, { passive: true });
        this.$destroy = () => window.removeEventListener('scroll', obs);
      },
    };
  });

});

/* ============================================================
   NON-ALPINE HELPERS
   ============================================================ */

/* Format price for display (Shopify returns cents) */
window.formatMoney = function(cents) {
  return 'R ' + (cents / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* Fetch variant ID from selected options */
window.getVariantFromOptions = function(variants, selectedOptions) {
  return variants.find(v => {
    return v.options.every((opt, i) => opt === selectedOptions[i]);
  });
};